/**
 * LayerCheckboxList
 *
 * Renders a list of toggleable layer rows (a checkbox + label per row) used by
 * the `LayersPanel` groups (authenticated + public read-only modes). The caller
 * owns its row CSS via the `rowClassName` prop; the mapping itself lives here
 * so the checkbox-row block isn't copy-pasted per group.
 *
 * Generic over the visibility key so it stays type-safe for both artifact
 * (`ArtifactVisibility`) and standard (`LayerVisibility`) groups.
 *
 * @module components/layers/LayerCheckboxList
 */
import { memo } from 'react';
import { Checkbox } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { LayerDefinition } from '../layerDefinitions';

interface LayerCheckboxListProps<TKey extends string> {
  /** Layer rows to render (store key + i18n label key). */
  items: LayerDefinition<TKey>[];
  /** Per-key checked state. */
  visibility: Record<TKey, boolean>;
  /** Called with the layer key when a row is toggled. */
  onToggle: (key: TKey) => void;
  /**
   * CSS class applied to each row wrapper (panel-owned). Typed loosely to match
   * CSS-module class lookups under `noUncheckedIndexedAccess`.
   */
  rowClassName: string | undefined;
}

function LayerCheckboxListComponent<TKey extends string>({
  items,
  visibility,
  onToggle,
  rowClassName,
}: LayerCheckboxListProps<TKey>) {
  const { t } = useTranslation();

  return (
    <>
      {items.map((layer) => (
        <div key={layer.key} className={rowClassName}>
          <Checkbox
            checked={visibility[layer.key]}
            onChange={() => onToggle(layer.key)}
            label={t(layer.labelKey)}
          />
        </div>
      ))}
    </>
  );
}

// `memo` drops the generic signature, so re-apply it on the exported binding.
export const LayerCheckboxList = memo(
  LayerCheckboxListComponent
) as typeof LayerCheckboxListComponent;
